import React, { useState, useEffect } from 'react';
import { Search, User, ArrowRight, X, Calendar } from 'lucide-react';
import api from '../services/api';

export default function ReservationSelectionModal({ show, onClose, onSelect }) {
    const [reservations, setReservations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');

    useEffect(() => {
        if (show) {
            fetchConfirmedReservations();
        }
    }, [show]);

    const fetchConfirmedReservations = async () => {
        setLoading(true);
        try {
            // [Fix] Buscar TODAS as reservas e filtrar no front (ou passar multiplos status se backend suportar)
            // Por enquanto, pegamos todas e filtramos aqui para 'confirmed' OU 'waiting_admission'
            const response = await api.get('/reservations');

            const validStatuses = ['confirmed', 'waiting_admission'];

            const viableReservations = response.data.filter(r =>
                validStatuses.includes(r.status)
            );

            setReservations(viableReservations);
        } catch (error) {
            console.error('Erro ao buscar reservas:', error);
        } finally {
            setLoading(false);
        }
    };

    // Filtrar localmente por nome do paciente ou setor
    const filtered = reservations.filter(r =>
        (r.patient_name || '').toLowerCase().includes(search.toLowerCase()) ||
        (r.sector?.name || '').toLowerCase().includes(search.toLowerCase())
    );

    if (!show) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-2xl border border-gray-200 dark:border-gray-800 flex flex-col max-h-[90vh]">

                {/* Header */}
                <div className="p-4 border-b border-gray-100 dark:border-gray-800 flex justify-between items-center bg-gray-50/50 dark:bg-gray-900">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                        <Search size={20} className="text-blue-600" />
                        Buscar Reserva NIR
                    </h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <X size={24} />
                    </button>
                </div>

                {/* Search Bar */}
                <div className="p-4 border-b border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                        <input
                            type="text"
                            placeholder="Buscar por nome do paciente ou setor..."
                            className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 focus:ring-2 focus:ring-blue-500 outline-none dark:bg-gray-800 dark:text-white"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            autoFocus
                        />
                    </div>
                </div>

                {/* List */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50 dark:bg-black/20">
                    {loading ? (
                        <div className="text-center py-8 text-gray-500">Carregando reservas...</div>
                    ) : filtered.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                            {search ? 'Nenhuma reserva encontrada para sua busca.' : 'Nenhuma reserva confirmada aguardando internação.'}
                        </div>
                    ) : (
                        filtered.map(res => (
                            <div
                                key={res.id}
                                onClick={() => { onSelect(res); onClose(); }}
                                className="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-700 cursor-pointer hover:border-blue-500 hover:shadow-md transition-all group"
                            >
                                <div className="flex justify-between items-center">
                                    <div className="flex items-center gap-4">
                                        <div className="p-3 bg-blue-50 text-blue-600 rounded-full dark:bg-blue-900/30 dark:text-blue-400 font-bold text-sm">
                                            #{res.id}
                                        </div>
                                        <div>
                                            <h4 className="font-bold text-gray-800 dark:text-white flex items-center gap-2">
                                                {res.patient_name || 'Paciente não informado'}
                                                {res.priority === 'Alta' && (
                                                    <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded border border-red-200">Alta Prioridade</span>
                                                )}
                                            </h4>
                                            <div className="text-sm text-gray-500 dark:text-gray-400 mt-1 flex items-center gap-3">
                                                <span className="flex items-center gap-1">
                                                    <User size={14} /> {res.patient_gender} ({res.patient_condition})
                                                </span>
                                                <span className="w-1 h-1 bg-gray-300 rounded-full"></span>
                                                <span className="flex items-center gap-1">
                                                    <Calendar size={14} /> {new Date(res.created_at).toLocaleDateString()}
                                                </span>
                                            </div>
                                            <div className="mt-2 text-xs font-semibold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 px-2 py-1 rounded inline-block">
                                                Destino: {res.sector?.name || 'Indefinido'}
                                                {res.specific_bed_id && ` (Leito sugerido)`}
                                            </div>
                                        </div>
                                    </div>
                                    <ArrowRight className="text-gray-300 group-hover:text-blue-500 transition-colors" />
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
}
